"use client";

import { useCallback } from "react";
import {
  MobileModalDebugEvent,
  type PersistentDebugLogger,
  captureMobileModalEnvironment,
} from "@/hooks/telemetry";
import type { MutableRefObject } from "react";
import { useTeamModal } from "@/contexts/team-modal";
import type {
  HomePageInteractionState,
  HomePageModalStateLocal,
} from "./domain/home-page/types";

interface HomePageGalleryHandlersLocal {
  handleAllTeamsClick: () => void;
  handleCloseTeamsModal: () => void;
  handleTeamsModalLoginRequest: () => void;
}

interface UseHomePageGalleryHandlersInput {
  latestInteractionState: MutableRefObject<HomePageInteractionState>;
  modalDebugLogger: PersistentDebugLogger;
  modalState: Pick<
    HomePageModalStateLocal,
    | "openAuthModal"
    | "setShowTeamsModal"
    | "showTeamDetailsModal"
    | "showTeamsModal"
    | "teamDetailsTeamId"
  >;
}

export function useHomePageGalleryHandlers({
  latestInteractionState,
  modalDebugLogger,
  modalState,
}: UseHomePageGalleryHandlersInput): HomePageGalleryHandlersLocal {
  const {
    openAuthModal,
    setShowTeamsModal,
    showTeamDetailsModal,
    showTeamsModal,
    teamDetailsTeamId,
  } = modalState;
  const { close: closeTeamModal } = useTeamModal();

  const handleAllTeamsClick = useCallback(() => {
    const { showTeamsModal, showTeamDetailsModal, teamDetailsTeamId } =
      latestInteractionState.current;
    modalDebugLogger.logEventFlow({
      name: MobileModalDebugEvent.AllTeamsClickOpenGallery,
      payload: {
        previousShowTeamsModal: showTeamsModal,
        previousShowTeamDetailsModal: showTeamDetailsModal,
        teamDetailsTeamId,
        environment: captureMobileModalEnvironment(),
      },
    });
    // An open team modal collapses through its own guarded close (URL cleanup
    // rides the controller's state) and the gallery opens only once that close
    // is ACCEPTED: cancelling a dirty discard leaves the modal standing, and
    // the gallery must not have opened underneath it in the meantime.
    closeTeamModal(() => setShowTeamsModal(true));
  }, [
    closeTeamModal,
    latestInteractionState,
    modalDebugLogger,
    setShowTeamsModal,
  ]);

  const handleCloseTeamsModal = useCallback(() => {
    modalDebugLogger.logEventFlow({
      name: MobileModalDebugEvent.TeamsGalleryCloseRequested,
      payload: {
        previousShowTeamsModal: showTeamsModal,
        showTeamDetailsModal,
        teamDetailsTeamId,
        environment: captureMobileModalEnvironment(),
      },
    });
    setShowTeamsModal(false);
  }, [
    modalDebugLogger,
    setShowTeamsModal,
    showTeamDetailsModal,
    showTeamsModal,
    teamDetailsTeamId,
  ]);

  const handleTeamsModalLoginRequest = useCallback(() => {
    modalDebugLogger.logEventFlow({
      name: MobileModalDebugEvent.TeamsGalleryLoginRequest,
      payload: {
        showTeamsModal,
        showTeamDetailsModal,
        teamDetailsTeamId,
        environment: captureMobileModalEnvironment(),
      },
    });
    setShowTeamsModal(false);
    openAuthModal();
  }, [
    modalDebugLogger,
    openAuthModal,
    setShowTeamsModal,
    showTeamDetailsModal,
    showTeamsModal,
    teamDetailsTeamId,
  ]);

  return {
    handleAllTeamsClick,
    handleCloseTeamsModal,
    handleTeamsModalLoginRequest,
  };
}
